import { MapPin, Plus, WarningCircleSolid } from '@byway/icons';
import { zodResolver } from '@hookform/resolvers/zod';
import clsx from 'clsx';
import Fuse, { type IFuseOptions } from 'fuse.js';
import { useTranslations } from 'next-intl';
import React, { useEffect, useMemo, useRef } from 'react';
import { Collection, Header, ListBoxSection } from 'react-aria-components';
import { type SubmitHandler, useForm } from 'react-hook-form';
import { useDebounce } from 'use-debounce';
import { z } from 'zod';
import { Stack } from '~/components/Layout/Stack';
import { api } from '~/libs/api';
import { usePlaygroundActions, usePlaygroundContext, usePlaygroundContextParams, usePlaygroundDestinations, usePlaygroundPreferences, usePlaygroundSelectedDestinations, usePlaygroundUIState } from '~/stores/playgroundStore';
import type { PlaygroundContext, PlaygroundDestinationSection, PlaygroundPlace } from '~/types/Playground.types';
import { logError, trackEvent } from '~/utils';
import { getCookie } from '~/utils/cookies';
import { sanitizeText } from '~/utils/sanitizeText';
import { Combobox, ComboboxItem, ComboboxItemIcon } from '../../Combobox';
import styles from './PlaygroundDestinationSelector.module.css';
const generateCustomDestinationId = () => {
  // Combine timestamp with random number to ensure uniqueness
  return -1000000000 - (Date.now() + Math.floor(Math.random() * 1000));
};
const ValidationSchema = z.object({
  destination: z.number()
});
const PLAYGROUND_MAX_RESULTS = 5;
type DestinationFormSchemaType = z.infer<typeof ValidationSchema>;
export const defaultFuseOptions = {
  threshold: 0.4,
  ignoreLocation: false,
  ignoreDiacritics: true
};
export function useFuzzySearch<T>(items: Array<T>, filterValue: string, options: IFuseOptions<T> = {
  ...defaultFuseOptions,
  keys: ['n', 'c']
}) {
  const fuse = useMemo(() => new Fuse(items, {
    keys: options.keys,
    threshold: options.threshold,
    ignoreLocation: options.ignoreLocation,
    ignoreDiacritics: options.ignoreDiacritics
  }), [items]);
  return fuse.search(filterValue).map(item => item.item);
}
export function PlaygroundDestinationSelector() {
  const t = useTranslations();
  const [filterValue, setFilterValue] = React.useState('');
  const [debouncedFilterValue, {
    isPending
  }] = useDebounce(filterValue, 400);
  const {
    isAddingDestination,
    removingDestination
  } = usePlaygroundUIState();
  const {
    addDestination,
    setContext,
    setLastAddedDestination,
    setIsAddingDestination,
    addUnsupportedDestination
  } = usePlaygroundActions();
  const playgroundDestinations = usePlaygroundDestinations();
  const {
    destinations: selectedDestinations,
    startDate,
    endDate,
    chosenOrigin
  } = usePlaygroundContextParams();
  const {
    nights
  } = usePlaygroundPreferences();
  const selectedDestinationIds = usePlaygroundSelectedDestinations().dpIds;
  const playgroundContext = usePlaygroundContext();
  const addDestinationCall = api.playground.addDestination.useMutation();
  const requestPlaceMaps = api.automation.requestPlaceMaps.useMutation();
  const {
    register,
    setValue,
    handleSubmit,
    reset
  } = useForm<DestinationFormSchemaType>({
    resolver: zodResolver(ValidationSchema)
  });
  const onSubmit: SubmitHandler<DestinationFormSchemaType> = async data => {
    setIsAddingDestination(true);
    try {
      const unsupportedPlace = playgroundDestinations.dpDestinations.unsupported.places.find(place => place.id === data.destination);
      const isUnsupported = !!unsupportedPlace;

      // Handle unsupported destination
      if (isUnsupported) {
        addUnsupportedDestination(unsupportedPlace);
        trackEvent('unsupported_destination_pinned', {
          event_category: 'Playground',
          event_label: unsupportedPlace.n ?? '',
          destination_id: data.destination
        });
        try {
          const displayCountry = getCookie('displayCountry');
          await requestPlaceMaps.mutateAsync({
            origin: chosenOrigin,
            destinations: selectedDestinations,
            unsupportedDestinations: [unsupportedPlace],
            startDate,
            endDate,
            nights: nights ?? null,
            displayCountry: displayCountry ? String(displayCountry) : 'Unknown'
          });
        } catch (error_0) {
          logError(error_0);
        }
        await new Promise(resolve => setTimeout(resolve, 10));
        return;
      }

      // Handle custom destination
      if (data.destination < -100000000) {
        const customPlace = {
          id: data.destination,
          n: sanitizeText(debouncedFilterValue),
          cm: null,
          s: false,
          c: {
            id: 'custom',
            cm: 'custom',
            n: 'Your place',
            s: false,
            p: []
          },
          geo: {
            latitude: 0,
            longitude: 0
          }
        };
        addUnsupportedDestination(customPlace);
        trackEvent('unsupported_destination_pinned', {
          event_category: 'Playground',
          event_label: sanitizeText(debouncedFilterValue),
          destination_id: 'custom'
        });
        try {
          const displayCountry_0 = getCookie('displayCountry');
          await requestPlaceMaps.mutateAsync({
            origin: chosenOrigin,
            destinations: selectedDestinations,
            unsupportedDestinations: [customPlace],
            startDate,
            endDate,
            nights: nights ?? null,
            displayCountry: displayCountry_0 ? String(displayCountry_0) : 'Unknown'
          });
        } catch (error_1) {
          logError(error_1);
        }
        await new Promise(resolve_0 => setTimeout(resolve_0, 10));
        return;
      }

      // Handle supported destination
      const res = await addDestinationCall.mutateAsync({
        playgroundInputContext: playgroundContext as PlaygroundContext,
        // TODO fix this, typing context almost there
        placeId: data.destination
      });
      if (!res) return; // TODO - throw error
      setContext(res);
      const placeName = playgroundDestinations.dpDestinations.selectable.places.find(place_0 => place_0.id === data.destination)?.n;
      trackEvent('destination_pinned', {
        event_category: 'Playground',
        event_label: placeName ?? '',
        destination_id: data.destination,
        source: 'dropdown'
      });
      addDestination(data.destination);
      setLastAddedDestination(data.destination);
    } catch (error) {
      logError(error);
    } finally {
      setIsAddingDestination(false);
      reset();
    }
  };
  const noResults = {
    id: -9999,
    // special ID for no results
    n: 'No results found',
    cm: null,
    s: false,
    c: {
      id: 'no results',
      cm: 'no results',
      n: 'no results',
      s: false,
      p: []
    },
    geo: {
      latitude: 0,
      longitude: 0
    }
  };
  const customDestinationOption = {
    id: generateCustomDestinationId(),
    // Generate unique ID for each custom destination
    n: `"${debouncedFilterValue}"`,
    cm: null,
    s: false,
    c: {
      id: 'custom',
      cm: 'custom',
      n: 'Your typed place',
      s: false,
      p: []
    },
    geo: {
      latitude: 0,
      longitude: 0
    }
  };
  const selectableDpPlaces = playgroundDestinations.dpDestinations.selectable.places.filter(place_1 => place_1 !== null && !selectedDestinationIds.includes(place_1.id));
  const unselectableDpPlaces = playgroundDestinations.dpDestinations.unselectable.places;

  // filter all unselectable and selectable places out from allCmsPlaces

  const filteredAllCmsPlaces = useFuzzySearch(playgroundDestinations.dpDestinations.unsupported.places, debouncedFilterValue);
  const filteredSelectableItems = useFuzzySearch(selectableDpPlaces, debouncedFilterValue);
  const filteredUnselectableItems = useFuzzySearch(unselectableDpPlaces, debouncedFilterValue);

  // Create sorted lists for each category (for when no filter value is entered)
  const allSortedSelectablePlaces = useMemo(() => {
    if (!selectableDpPlaces || selectableDpPlaces.length === 0) return [];
    return selectableDpPlaces.slice().sort((a, b) => {
      return a.n.localeCompare(b.n, undefined, {
        sensitivity: 'base'
      });
    });
  }, [selectableDpPlaces]);
  const allSortedUnselectablePlaces = useMemo(() => {
    if (!unselectableDpPlaces || unselectableDpPlaces.length === 0) return [];
    return unselectableDpPlaces.slice().sort((a_0, b_0) => {
      return a_0.n.localeCompare(b_0.n, undefined, {
        sensitivity: 'base'
      });
    });
  }, [unselectableDpPlaces]);
  const allSortedUnsupportedPlaces = useMemo(() => {
    const unsupportedPlaces = playgroundDestinations.dpDestinations.unsupported.places;
    if (!unsupportedPlaces || unsupportedPlaces.length === 0) return [];
    return unsupportedPlaces.slice().sort((a_1, b_1) => {
      return a_1.n.localeCompare(b_1.n, undefined, {
        sensitivity: 'base'
      });
    });
  }, [playgroundDestinations.dpDestinations.unsupported.places]);

  // Build collection for RAC component

  const sections: PlaygroundDestinationSection[] = filterValue === '' || isPending() || isAddingDestination || (removingDestination?.size ?? 0) > 0 ? filterValue === '' ? [{
    name: 'Selectable places',
    items: allSortedSelectablePlaces
  }, {
    name: 'Unreachable places',
    items: allSortedUnselectablePlaces
  }, {
    name: 'Unsupported places',
    altHeading: t('playground.places_add_new', {
      count: allSortedUnsupportedPlaces.length
    }),
    items: allSortedUnsupportedPlaces
  }].filter(section => section.items.length > 0) : [] : [{
    name: 'Selectable places',
    items: filteredSelectableItems.length > 0 ? filteredSelectableItems.slice(0, PLAYGROUND_MAX_RESULTS) : [noResults]
  }, {
    name: 'Unreachable places',
    items: filteredUnselectableItems.length > 0 ? filteredUnselectableItems.slice(0, PLAYGROUND_MAX_RESULTS) : [noResults]
  },
  // collection for places not yet available in maps, but approved and in the CMS
  {
    name: 'Unsupported places',
    items: [...(filteredAllCmsPlaces.length > 0 ? filteredAllCmsPlaces.slice(0, PLAYGROUND_MAX_RESULTS) : []), ...(debouncedFilterValue.length >= 2 && !filteredSelectableItems.some(place_4 => place_4.n.toLowerCase() === debouncedFilterValue.toLowerCase()) && !filteredUnselectableItems.some(place_3 => place_3.n.toLowerCase() === debouncedFilterValue.toLowerCase()) && !filteredAllCmsPlaces.some(place_2 => place_2.n.toLowerCase() === debouncedFilterValue.toLowerCase()) ? [customDestinationOption] : [])].filter(Boolean) // Remove any undefined/null items
  }].filter(section_0 => section_0.items.length > 0); // Only keep sections that have items

  const formRef = useRef<HTMLFormElement>(null);
  useEffect(() => {
    if (!debouncedFilterValue || debouncedFilterValue.length < 3 || isPending()) return;
    if (filteredSelectableItems.length > 0 || filteredUnselectableItems.length > 0) {
      trackEvent('destination_search', {
        event_category: 'Playground',
        event_label: debouncedFilterValue,
        search_term: debouncedFilterValue
      });
    }
    if (filteredSelectableItems.length === 0 && filteredUnselectableItems.length === 0) {
      trackEvent('destination_search_no_results', {
        event_category: 'Playground',
        event_label: debouncedFilterValue,
        search_term: debouncedFilterValue
      });
    }
    return;
  }, [debouncedFilterValue]);
  return <form ref={formRef} onSubmit={handleSubmit(onSubmit)} onKeyDown={e => {
    if (e.key === 'Enter' && e.target !== formRef.current?.querySelector('input')) {
      e.preventDefault();
      e.stopPropagation();
    }
  }}>
      <Combobox inputValue={filterValue} onInputChange={setFilterValue} isDisabled={isAddingDestination || (removingDestination?.size ?? 0) > 0} label={t('playground.places_search')} placeholder={t('playground.places_search_placeholder')} formValue="key" items={sections} allowsEmptyCollection={true} {...register('destination')} onSelectionChange={item => {
      if (!item) {
        return;
      }
      setValue('destination', item as number);
      handleSubmit(onSubmit)();
      setFilterValue('');
    }} hasButton>
        {(section_1: PlaygroundDestinationSection) => {
        return section_1.items.length > 0 && <ListBoxSection className={clsx({
          [styles.section]: true
        })} id={section_1.name}>
                <Header className={styles.label}>{section_1?.altHeading || section_1.name}</Header>
                <Collection items={section_1.items}>
                  {(destination: PlaygroundPlace) => {
              const isDisabled = destination.id === -9999 || section_1.name === 'Unreachable places';
              return <ComboboxItem textValue={destination.n} id={destination.id} key={destination.id} isDisabled={isDisabled}>
                        {destination.id === -9999 ? <p className={clsx(styles.placeName, styles.grey)}>
                            {debouncedFilterValue ? t('content.feedback.search_generic_empty') : ''}
                          </p> : <>
                            <div className={clsx({
                    [styles.pinAndTextContainer]: true,
                    [styles.disabled]: isDisabled
                  })}>
                              <ComboboxItemIcon>
                                <MapPin size="large" decorative />
                              </ComboboxItemIcon>
                              <div>
                                <Stack variant="horizontal" space="--by-space-200">
                                  <h2 className={clsx({
                          [styles.placeName]: true,
                          [styles.disabled]: isDisabled
                        })}>
                                    {destination.n}
                                  </h2>
                                </Stack>
                                <span className={clsx({
                        [styles.countryName]: true,
                        [styles.disabled]: isDisabled
                      })}>
                                  {typeof destination.c === 'string' ? destination.c : destination.c?.n}
                                </span>
                              </div>
                            </div>

                            {(section_1.name === 'Selectable places' || section_1.name === 'Unsupported places') && <div className={styles.icon}>
                                <Plus decorative />
                              </div>}
                          </>}
                      </ComboboxItem>;
            }}
                </Collection>
              </ListBoxSection>;
      }}
      </Combobox>

      <div className={styles.errorsContainer}>
        {addDestinationCall.error && <p className={styles.error}>
            <WarningCircleSolid decorative />
            {t('content.feedback.place_add_error_recovery')}
          </p>}
      </div>
    </form>;
}